import { formatEther, formatUnits, parseEther } from '@ethersproject/units';
import { BigNumber } from 'ethers';
import moment from 'moment';
import { useContext, useMemo, useState } from 'react';
import Button from '../../components/Button';
import CustomModal from '../../components/CustomModal';
import CustomModalHeader from '../../components/CustomModal/CustomModalHeader';
import InfoPopUp from '../../components/InfoPopUp';
import TextInput from '../../components/TextInput';
import {
    EXTEND_INCENTIVES_PENDING,
    INCENTIVES_EXTENDED,
    INCENTIVES_INCREASED,
    INCENTIVES_REINCENTIVIZED,
    IncentivizeSpoolTypes,
    INCREASE_INCENTIVES_PENDING,
    INSUFFICIENT_FUNDS_ERROR,
    LOWER_INCENTIVE_RATE_ERROR,
    REINCENTIVIZING_PENDING,
    TimeConversions,
} from '../../config/constants';
import appContext from '../../context/app/appContext';
import errorContext from '../../context/error/errorContext';
import modalContext from '../../context/modal/modalContext';
import { formatRemainingTime, formatUSD } from '../../helpers/formats';
import useSpoolsCreatedByUser from '../../hooks/web3/app/useSpoolsCreatedByUser';
import useUserBalance from '../../hooks/web3/useUserBalance';
import { preventOverflow, sleep, toBigNumber } from '../../web3/utils';

const MODAL_DETAILS = {
    [IncentivizeSpoolTypes.REINCENTIVIZE]: {
        title: 'Re-Incentivize Your Spool',
        details: `Your Spool’s Incentive Rewards period for this token has been completed, but can be re-incentivized at any rate you prefer.`,
        buttonText: 'Re-Incentivize Spool',
        waiting: REINCENTIVIZING_PENDING,
        completed: INCENTIVES_REINCENTIVIZED,
    },
    [IncentivizeSpoolTypes.EXTEND]: {
        title: 'Extend Your Spool’s Incentives',
        details: `Extend the length of time your Spool’s distributes Incentive Token rewards at the current daily rate.`,
        buttonText: 'Extend Incentive Token Rewards',
        waiting: EXTEND_INCENTIVES_PENDING,
        completed: INCENTIVES_EXTENDED,
    },
    [IncentivizeSpoolTypes.INCREASE]: {
        title: 'Increase Your Spool’s Current Incentives Rate',
        details: `Increase the rate of your Spool’s current Incentive Token rewards for the remaining distribution period.`,
        buttonText: 'Increase Incentive Token Rewards',
        waiting: INCREASE_INCENTIVES_PENDING,
        completed: INCENTIVES_INCREASED,
    },
};

const ReIncentivizeSpool: React.FC = () => {
    const { modalData, closeModal, openModal, setModalData } = useContext(modalContext);

    const { setError } = useContext(errorContext);

    const { interfaceSDK, selectedSpool } = useContext(appContext);

    const { updateOnIncentivize } = useSpoolsCreatedByUser();

    const onClose = () => {
        closeModal('incentivizeSpoolModal');
        setIncentivizeDetails(initialState);
    };

    const initialState = useMemo(() => ({ amount: '', duration: '' }), []);

    const [incentivizeDetails, setIncentivizeDetails] = useState(initialState);

    const userBalance = useUserBalance(modalData?.incentivizedReward?.id);

    if (!modalData.incentivizedReward) return null;

    const {
        id,
        symbol,
        remainingAmount,
        totalAmount,
        emissionStartDate,
        emissionEndDate,
        totalEmissionDays,
        decimals,
    } = modalData.incentivizedReward;

    const timeRemaining = moment.unix(+emissionEndDate).isAfter(moment())
        ? moment.unix(+emissionEndDate).diff(moment(), 'seconds')
        : 0;

    const totalDuration = moment
        .unix(+emissionEndDate)
        .diff(moment.unix(+emissionStartDate), 'seconds');

    const currentRate =
        +incentivizeDetails.amount && +incentivizeDetails.duration
            ? parseEther(incentivizeDetails.amount)
                  .add(parseEther(remainingAmount))
                  .mul(parseEther('1'))
                  .div(
                      parseEther(incentivizeDetails.duration).add(
                          modalData.type === IncentivizeSpoolTypes.EXTEND
                              ? parseEther(timeRemaining.toString()).div(
                                    TimeConversions.SECONDS_PER_DAY
                                )
                              : '0'
                      )
                  )
            : BigNumber.from('0');

    const previousRate = parseEther(totalAmount)
        .mul(TimeConversions.SECONDS_PER_DAY)
        .div(BigNumber.from(totalDuration));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (modalData.type === IncentivizeSpoolTypes.EXTEND) {
            // we know for sure that the only changeable field is number of days

            setIncentivizeDetails((prev) => ({
                ...prev,
                [name]: preventOverflow(value, 0),
                amount: value
                    ? preventOverflow(
                          formatEther(
                              previousRate
                                  .mul(
                                      parseEther(value).add(
                                          // adding 1 here as a buffer. Sometimes the  amount calculated is slighty lower possibly due to the millisecond passed upon calculating.
                                          parseEther((timeRemaining + 1).toString()).div(
                                              TimeConversions.SECONDS_PER_DAY
                                          )
                                      )
                                  )
                                  .div(parseEther('1'))
                                  .sub(parseEther(remainingAmount))
                          ),
                          decimals
                      )
                    : '0',
            }));

            return;
        }

        if (modalData.type === IncentivizeSpoolTypes.INCREASE) {
            // we know for sure that the only changeable field is amount of tokens

            setIncentivizeDetails((prev) => ({
                ...prev,
                amount: preventOverflow(value, decimals),
                duration: (timeRemaining / TimeConversions.SECONDS_PER_DAY).toString(),
            }));

            return;
        }

        setIncentivizeDetails((prev) => ({
            ...prev,
            [name]: name === 'amount' ? preventOverflow(value, decimals) : value,
        }));
    };

    const handleMaxInput = () => {
        if (!userBalance || !interfaceSDK) return;

        setIncentivizeDetails((prev) => ({
            ...prev,
            amount: formatUnits(userBalance, decimals),
        }));
    };

    const handleClick = async () => {
        if (!userBalance || !selectedSpool || !id || !interfaceSDK) return;

        if (currentRate.lt(previousRate)) {
            openModal('errorModal');
            setModalData((prev) => ({
                ...prev,
                message: LOWER_INCENTIVE_RATE_ERROR,
            }));
            return;
        }

        if (userBalance.lt(toBigNumber(incentivizeDetails.amount, decimals))) {
            openModal('errorModal');
            setModalData((prev) => ({ ...prev, message: INSUFFICIENT_FUNDS_ERROR }));
            return;
        }

        try {
            openModal('waitingModal');
            setModalData((prev) => ({
                ...prev,
                message: MODAL_DETAILS[modalData.type as IncentivizeSpoolTypes]?.waiting,
            }));

            const vaultContract = interfaceSDK.get.getVaultContractInstance(
                selectedSpool.address
            );

            const incentivize = await vaultContract.notifyRewardAmount(
                id,
                toBigNumber(incentivizeDetails.amount, decimals),
                (
                    +incentivizeDetails.duration * TimeConversions.SECONDS_PER_DAY +
                    timeRemaining
                ).toFixed(0)
            );

            const receipt = await incentivize.wait();

            await sleep(3000);

            await updateOnIncentivize(
                selectedSpool,
                modalData.type as IncentivizeSpoolTypes,
                id
            );

            openModal('successModal');
            setModalData((prev) => ({
                ...prev,
                status: MODAL_DETAILS[modalData.type as IncentivizeSpoolTypes]?.completed,
                message: '',
                txHash: receipt.transactionHash,
            }));
            onClose();
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <CustomModal
            modalName='incentivizeSpoolModal'
            className={`incentivize-spool ${modalData.type.toLowerCase()}`}
        >
            <CustomModalHeader
                title={
                    MODAL_DETAILS[modalData.type as IncentivizeSpoolTypes]?.title ??
                    'Incentivize Spool'
                }
                onClose={onClose}
            />
            <div className='note'>
                {MODAL_DETAILS[modalData.type as IncentivizeSpoolTypes]?.details}
            </div>
            <div className='incentivize-details'>
                <div className='remaining-amount'>
                    <span>Remaining {symbol} to be Emitted</span>
                    <div className='amount'>
                        <strong>{formatUSD(remainingAmount || 0)} </strong>
                        out of{' '}
                        <strong>
                            {formatUSD(totalAmount || 0)} {symbol}
                        </strong>
                    </div>
                </div>
                <div className='remaining-duration'>
                    <span>Time Remaining Until Completion</span>
                    <div className='duration'>
                        <strong>
                            {moment.unix(+emissionEndDate).isAfter(moment())
                                ? formatRemainingTime(timeRemaining).timeAndUnit
                                : 0}{' '}
                        </strong>
                        out of <strong>{totalEmissionDays}</strong>
                    </div>
                </div>
            </div>
            <div className='content'>
                <div className='content-inner'>
                    <div className='field-container token-amount'>
                        <div className='field'>
                            Number of {symbol} to{' '}
                            {modalData.type === IncentivizeSpoolTypes.REINCENTIVIZE
                                ? 'incentivize'
                                : 'be added'}
                        </div>
                        <div className='input-container'>
                            {modalData.type === IncentivizeSpoolTypes.EXTEND ? (
                                formatUSD(
                                    parseEther(incentivizeDetails.amount || '0').gt('0')
                                        ? incentivizeDetails.amount
                                        : 0,
                                    4
                                )
                            ) : (
                                <>
                                    <TextInput
                                        onChange={handleChange}
                                        value={incentivizeDetails.amount}
                                        name='amount'
                                        placeholder='0.00'
                                    />
                                    <Button variant='secondary' onClick={handleMaxInput}>
                                        MAX
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                    <div className='field-container balance-in-wallet'>
                        <div className='field'>{symbol} in Wallet</div>
                        <div>
                            {formatUSD(
                                userBalance ? formatUnits(userBalance, decimals) : 0,
                                4
                            )}
                        </div>
                    </div>
                    <div className='field-container duration'>
                        <div className='field'>
                            {modalData.type === IncentivizeSpoolTypes.INCREASE
                                ? 'Current Remaining Incentive Period'
                                : `Number of Days to ${
                                      modalData.type === IncentivizeSpoolTypes.EXTEND
                                          ? 'Extend'
                                          : 'Emit'
                                  }`}
                        </div>
                        <div className='input-container'>
                            {modalData.type === IncentivizeSpoolTypes.INCREASE ? (
                                formatRemainingTime(timeRemaining).timeAndUnit
                            ) : (
                                <TextInput
                                    onChange={handleChange}
                                    value={incentivizeDetails.duration}
                                    name='duration'
                                    placeholder='30'
                                />
                            )}
                        </div>
                    </div>
                    <div className='field-container incentivize-rate'>
                        <div className='field'>New Daily Rate</div>
                        <div>
                            <div className='rate'>
                                {formatUSD(formatEther(currentRate || '0'))} {symbol}
                                /DAY
                            </div>
                            <div className='previous-rate'>
                                {formatUSD(formatEther(previousRate || '0'))} {symbol}
                                /DAY (Previous){' '}
                                <InfoPopUp>
                                    Incentive rates can only be decreased after current
                                    incentives have been completed.
                                </InfoPopUp>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='button-container'>
                <Button onClick={handleClick}>
                    {MODAL_DETAILS[modalData.type as IncentivizeSpoolTypes]?.buttonText ??
                        'Incentivize'}
                </Button>
            </div>
        </CustomModal>
    );
};

export default ReIncentivizeSpool;
